import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { getHeaderHtml, parseHeader, drawReportHeader, drawAuthorizedSignatory } from "../../../../util/headerUtil";

export const printBillStatementReceipt = (
  patientData,
  serviceItems = [],
  totalBill,
  amountReceived,
  outstandingAmount,
  billingDate,
  paymentType,
  txnNo,
  authCtx,
  billId
) => {
  const hospitalData = authCtx?.hospitalData || authCtx || {};
  const printWindow = window.open("", "_blank", "width=900,height=650");
  if (!printWindow) return;

  const calculatedTotal =
    totalBill && totalBill > 0
      ? totalBill
      : serviceItems.reduce((sum, item) => sum + (item.netAmount || 0), 0);

  printWindow.document.write(`
    <html>
      <head>
        <title>Bill Receipt</title>
        <style>
          body { font-family: 'Arial', sans-serif; margin: 0; padding: 20px; color: #000; }
          .header-container { text-align: center; }
          .separator { height: 1.5px; background: #000; margin: 10px 0; }
          .patient-info { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 13px; font-weight: bold; }
          .info-row { display: flex; justify-content: space-between; padding: 2px 0; }
          
          table { width: 100%; border-collapse: collapse; margin-top: 5px; }
          th, td { border: 1px solid #000; padding: 5px; text-align: left; font-size: 12px; }
          th { background: transparent; font-weight: bold; }
          .total-row td { font-weight: bold; text-align: right; }
          
          .summary-section { display: flex; justify-content: space-between; margin-top: 15px; font-size: 13px; font-weight: bold; }
          .payment-info { margin-top: 10px; font-size: 13px; font-weight: bold; }
          
          .footer-sign { margin-top: 80px; float: right; width: 250px; text-align: center; }
          .sig-line { border-top: 1px solid #000; margin-bottom: 5px; }
          @media print {
            body { padding: 10px; }
            .footer-sign {
              position: fixed;
              bottom: 20mm;
              right: 14mm;
              margin-top: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="header-container">
          ${getHeaderHtml(hospitalData)}
        </div>
        <div class="separator"></div>
        
        <div class="patient-info">
          <div style="width: 60%;">
            <div class="info-row">
              <span>Name: ${patientData?.firstName || ""} ${patientData?.lastName || ""}</span>
            </div>
            <div class="info-row">
              <span>Address: ${patientData?.address || "-"}</span>
            </div>
          </div>
          <div style="width: 40%; text-align: right;">
            <div class="info-row" style="justify-content: flex-end;">
              <span>Bill No: ${billId || "-"}</span>
            </div>
            <div class="info-row" style="justify-content: flex-end;">
              <span>Mobile: ${patientData?.mobile || "-"}</span>
            </div>
            <div class="info-row" style="justify-content: flex-end;">
              <span>Date: ${billingDate ? new Date(billingDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : "-"}</span>
            </div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 50px;">Sr No</th>
              <th>Expense</th>
              <th style="width: 80px;">Amount</th>
              <th style="width: 70px;">Quantity</th>
              <th style="width: 80px;">Total Amt</th>
              <th style="width: 100px;">Discount(%)</th>
              <th style="width: 90px;">Net Amt</th>
            </tr>
          </thead>
          <tbody>
            ${serviceItems && serviceItems.length > 0
      ? serviceItems.map((item, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${item.serviceDescription || "-"}</td>
                  <td>${Number(item.actualAmount || 0).toFixed(2)}</td>
                  <td>${item.quantity ?? 1}</td>
                  <td>${(Number(item.actualAmount || 0) * Number(item.quantity || 1)).toFixed(2)}</td>
                  <td>${item.discount ?? 0}</td>
                  <td>${Number(item.netAmount || 0).toFixed(2)}</td>
                </tr>`
      ).join("")
      : `<tr><td colspan="7" style="text-align:center;">No services found</td></tr>`
    }
            <tr class="total-row">
              <td colspan="6">Total</td>
              <td>${Number(calculatedTotal).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        <div class="summary-section">
          <div>Amount Received: ${Number(amountReceived || 0).toFixed(2)}</div>
          <div>Outstanding Amount: ${Number(outstandingAmount || 0).toFixed(2)}</div>
        </div>

        <div class="payment-info">
          Payment Type: ${paymentType || "-"}
        </div>

        <div class="footer-sign">
          <div class="sig-line"></div>
          <p style="margin: 0; font-weight: bold; font-size: 13px;">Authority Sign / Doctor Sign</p>
        </div>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 500);
};

const buildPDF = (doc, patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId) => {
  const hospitalData = authCtx?.hospitalData || authCtx || {};
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  const startY = drawReportHeader(doc, hospitalData, "", {
    margin: 0,
    top: 0,
    hideSeparator: true,
    isExport: true,
    contentMargin: 40
  });

  let currentY = startY + 10;

  // Title
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("BILL RECEIPT", pageWidth / 2, currentY, { align: "center" });

  currentY += 15;

  // Patient Info section
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");

  // Row 1: Name & Date
  doc.text(`Name: ${patientData?.firstName || ""} ${patientData?.lastName || ""}`, margin, currentY);
  const dateStr = billingDate ? new Date(billingDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : "-";
  doc.text(`Date: ${dateStr}`, pageWidth - margin, currentY, { align: "right" });

  currentY += 8;

  // Row 2: Bill No & Mobile
  doc.text(`Bill No: ${billId || "-"}`, margin, currentY);
  doc.text(`Mobile: ${patientData?.mobile || "-"}`, pageWidth - margin, currentY, { align: "right" });

  currentY += 8;

  const tableData = serviceItems.map((item, index) => [
    index + 1,
    item.serviceDescription || "-",
    Number(item.actualAmount || 0).toFixed(2),
    item.quantity ?? 1,
    ((item.actualAmount ?? 0) * (item.quantity ?? 1)).toFixed(2),
    item.discount ?? 0,
    Number(item.netAmount || 0).toFixed(2)
  ]);

  doc.autoTable({
    startY: currentY,
    head: [
      ["Sr. No", "Expense", "Amount", "Quantity", "Total Amt", "Discount(%)", "Net Amt"]
    ],
    body: tableData,
    foot: [
      [{ content: "Total", colSpan: 6, styles: { halign: 'right' } }, parseFloat(totalBill || 0).toFixed(2)]
    ],
    theme: "grid",
    styles: {
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      fontSize: 10,
      textColor: [0, 0, 0]
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      halign: "center"
    },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold'
    }
  });

  currentY = doc.lastAutoTable.finalY + 12;

  doc.setFont("helvetica", "bold");
  doc.text(`Amount Received: ${Number(amountReceived || 0).toFixed(2)}`, margin, currentY);
  doc.text(`Outstanding Amount: ${Number(outstandingAmount || 0).toFixed(2)}`, pageWidth - margin, currentY, { align: "right" });

  currentY += 8;
  doc.text(`Payment Type: ${paymentType || "-"}`, margin, currentY);

  const sigY = pageHeight - 30;
  doc.line(pageWidth - 70, sigY, pageWidth - 15, sigY);
  doc.setFontSize(10);
  doc.text("Authority Sign / Doctor Sign", pageWidth - 42.5, sigY + 5, { align: "center" });

  const userRole = authCtx?.role;
  const userName = authCtx?.userName;
  if (userRole === "ROLE_DOCTOR" && userName) {
    doc.setFontSize(8);
    doc.text(`( ${userName} )`, pageWidth - 42.5, sigY + 14, { align: "center" });
  }
};

const buildPDFA5 = (doc, patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId) => {
  const hospitalData = authCtx?.hospitalData || authCtx || {};
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const startY = drawReportHeader(doc, hospitalData, "Bill Receipt", { margin: 0, top: 0, hideSeparator: true, isExport: true });

  let currentY = startY + 5;

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.text(
    `Name: ${patientData?.firstName || ""} ${patientData?.lastName || ""}`,
    14,
    currentY
  );
  const dateStr = billingDate ? new Date(billingDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : "-";
  doc.text(`Date: ${dateStr}`, pageWidth - 60, currentY);

  currentY += 6;
  doc.text(`Bill No: ${billId || "-"}`, 14, currentY);
  doc.text(`Mobile: ${patientData?.mobile || "-"}`, pageWidth - 60, currentY);

  currentY += 6;
  doc.text(
    `Address: ${patientData?.address || "-"}`,
    14,
    currentY
  );

  currentY += 8;
  const tableData = serviceItems.map((item, index) => [
    index + 1,
    item.serviceDescription || "-",
    item.actualAmount ?? 0,
    item.quantity ?? 1,
    ((item.actualAmount ?? 0) * (item.quantity ?? 1)).toFixed(2),
    item.discount ?? 0,
    item.netAmount ?? 0
  ]);

  doc.autoTable({
    startY: currentY,
    head: [
      ["Sr. No", "Expense", "Amount", "Quantity", "Total Amt", "Discount(%)", "Net Amt"]
    ],
    body: tableData,
    foot: [
      ["", "", "", "", "", "Total", parseFloat(totalBill || 0).toFixed(2)]
    ],
    theme: "grid",
    styles: {
      fontSize: 7,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      halign: "center",
      lineWidth: 0.1,
      lineColor: [0, 0, 0]
    },
    bodyStyles: { fillColor: [255, 255, 255] },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      halign: "right",
      lineWidth: 0.1,
      lineColor: [0, 0, 0]
    }
  });

  const finalY = doc.lastAutoTable.finalY + 8;
  doc.text(`Amount Received: ${amountReceived || 0}`, 14, finalY);
  doc.text(`Outstanding Amount: ${outstandingAmount || 0}`, pageWidth - 60, finalY);

  let nextY = finalY + 6;
  doc.text(`Payment Type: ${paymentType || "-"}`, 14, nextY);
  if (paymentType && paymentType.toLowerCase() !== "cash") {
    doc.text(`Txn No: ${txnNo || "-"}`, pageWidth - 60, nextY);
  }

  drawAuthorizedSignatory(doc, hospitalData);

  const userName = authCtx?.userName;
  if (authCtx?.role === "ROLE_DOCTOR" && userName) {
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`( ${userName} )`, pageWidth - 35, pageHeight - 8, { align: "center" });
  }
};

export const exportBillPDF = (patientData, serviceItems = [], totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, format = "a4", authCtx, billId) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: format === "a5" ? "a5" : "a4",
  });

  if (format === "a5") {
    buildPDFA5(doc, patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId);
  } else {
    buildPDF(doc, patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId);
  }

  const patientName = `${patientData?.firstName || "Patient"}_${patientData?.lastName || ""}`.trim();
  const safeName = patientName.replace(/\s+/g, "_") || "patient";
  doc.save(`${safeName}_bill.pdf`);
};

export const shareBillViaWhatsApp = async (patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId) => {
  const hospitalData = authCtx?.hospitalData;
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });
  buildPDF(doc, patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId);

  const pdfBlob = doc.output("blob");
  const patientMobile = patientData?.mobile || "";
  const patientName = `${patientData?.firstName || ""} ${patientData?.lastName || ""}`.trim() || "Patient";
  const cleanMobile = patientMobile.replace(/\D/g, '');
  const hospitalName = hospitalData?.hospitalName || "the hospital";

  // Format items for WhatsApp message
  const itemDetails = serviceItems && serviceItems.length > 0
    ? serviceItems.map((item, index) => {
      const srNo = index + 1;
      const desc = item.serviceDescription || "-";
      const actualAmount = parseFloat(item.actualAmount || 0).toFixed(2);
      const qty = item.quantity || 1;
      const totalAmt = (parseFloat(actualAmount) * parseFloat(qty)).toFixed(2);
      const discount = item.discount || 0;
      const netAmt = (item.netAmount ?? ((item.actualAmount || 0) * qty)).toFixed(2);

      return `${srNo} | ${desc} | ${actualAmount} | ${qty} | ${totalAmt} | ${discount} | ${netAmt}`;
    }).join('\n')
    : "No items";

  const total = parseFloat(totalBill || 0).toFixed(2);

  const message = `Hello ${patientName}, your bill receipt from ${hospitalName} is ready.
*Bill No:* ${billId || "-"}

*Sr.No | Expense | Amount | Quantity | Total Amt | Discount(%) | Net Amt*
${itemDetails}
--------------------------------
*Total | ${total}*

regards
${hospitalName}`;

  // 📥 On Desktop: Always trigger download so it's ready to drag-and-drop into WhatsApp Web
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (!isMobile) {
    doc.save(`${patientName}_Bill.pdf`);
  }

  // Priority: Open WhatsApp directly (Web or App)
  const waUrl = `https://api.whatsapp.com/send?phone=91${cleanMobile}&text=${encodeURIComponent(message)}`;
  window.open(waUrl, "_blank");

  // Optional: Native share only for mobile
  if (isMobile && navigator.share && navigator.canShare) {
    const file = new File([pdfBlob], `${patientName}_Bill.pdf`, { type: "application/pdf" });
    if (navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'Bill Receipt',
          text: message,
        });
      } catch (error) {
        console.log("Native share cancelled");
      }
    }
  }
};
